import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { SnackType } from '@/interfaces/snack'
import { showErrors, showNotification } from '@/utils/snack'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppTextarea from '@/components/AppTextarea.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppPhoneInput from '@/components/AppPhoneInput.vue'
import { User } from '@/modules/User/interfaces/user'
import { getUsers, newUser, editUser, deleteUserById, resetUserPassword } from '@/modules/User/gql/user.gql'
import { State } from 'vuex-class'
import { formatPhoneNumber } from '@/utils/phoneNumberFormat'
import delNonNumCode from '@/utils/delNonNumCode'

@Component({
  components: {
    AppBreadcrumbs,
    AppCheckbox,
    AppSearch,
    AppDialog,
    AppTextField,
    AppTextarea,
    AppSelector,
    AppDatePicker,
    AppPhoneInput
  }
})

export default class UserList extends Vue {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Setting'
    },
    {
      text: 'User'
    }
  ]

  onlyApplied = false

  searchVal = ''

  newUserInput = {
    id: '',
    username: '',
    emails: '',
    password: '',
    passwordRetype: '',
    userType: -1,
    contactName: '',
    contactPhoneSplice: '',
    contactFaxSplice: '',
    contactEmail: ''
  }

  newUserValid = true

  disableConfirm = false

  users = []

  windowSize = {
    w: 0,
    h: 0
  }

  orderBy: {
    [key: string]: string
  } = {
    username: 'USERNAME',
    email: 'EMAIL',
    id: 'ID'
  }

  newUserDialog = false
  editUserDialog = false
  deleteDialog = false
  editUserValid = true
  resetPasswordValid = true

  editDialogTitles = ['Edit User Information', 'Change Password']
  editDialogHeight = [606, 366]
  editDialogTab = 0

  renaming = false
  nullUsername = false

  tableColumn = [
    { field: 'username', title: 'Users', sortable: true, width: 200, slots: { default: 'name_default' } },
    { field: 'email',
      title: 'Email',
      sortable: true,
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.emails ? this.emailArrToStr(row.emails) : '-')
        ]
      }
      }
    },
    {
      field: 'userType',
      title: 'Role',
      sortable: true,
      resizable: true
    },
    {
      field: 'contactName',
      title: 'Contact Name',
      sortable: true,
      resizable: true,
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.contactName || '-')
          ]
        }
      }
    },
    {
      field: 'contactPhone',
      title: 'Phone',
      sortable: true,
      resizable: true,
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.contactPhone ? formatPhoneNumber(row.contactPhone, row.phoneCountryCode) : '-')
          ]
        }
      }
    },
    { title: 'Ext',
      field: 'phoneExtensionNumber',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.phoneExtensionNumber || '-')
        ]
      }
      },
      width: 80,
      resizable: true,
      sortable: false
    },
    {
      field: 'contactFax',
      title: 'Fax',
      sortable: true,
      resizable: true,
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.contactFax ? formatPhoneNumber(row.contactFax, row.faxCountryCode) : '-')
          ]
        }
      }
    },
    { title: 'Fax Ext',
      field: 'faxExtensionNumber',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.faxExtensionNumber || '-')
        ]
      }
      },
      width: 80,
      resizable: true,
      sortable: false
    },
    {
      field: 'contactEmail',
      title: 'Contact Email',
      sortable: true,
      resizable: true,
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.contactEmail || '-')
          ]
        }
      }
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 112, resizable: false }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'name',
    sortDesc: true
  }

  get userTypeOptions () {
    return this.me.userType === 'ROOT'
      ? ['ROOT', 'MANAGER', 'AGENT']
      : ['MANAGER', 'AGENT']
  }

  @State(state => state.user.me) me!: User

  mounted () {
    this.onResize()
    this.getUsers()
  }

  @Watch('onlyApplied')
  onOnlyAppliedChanged (val: boolean, oldVal: boolean) {
    this.getUsers()
  }

  @Watch('newUserInput.username')
  onUsernameChanged (val: string) {
    if (!this.nullUsername) {
      return
    }
    if (val !== '') {
      this.nullUsername = false
    }
  }

  async getUsers () {
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'USERNAME'
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getUsers,
        variables: {
          filter: {
            username: searchVal
          },
          order: {
            orderBy: orderBy,
            asc: sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.users = result.data.getUsers.users
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getUsers.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  onResize () {
    this.windowSize = { w: window.innerWidth, h: window.innerHeight }
  }

  async newUserConfirm () {
    const {
      username, password, emails,
      contactName, contactEmail,
      contactFaxSplice, contactPhoneSplice
    } = this.newUserInput
    const newUserValid = (this.$refs.newUserForm as Vue & { validate: () => boolean }).validate()
    if (!newUserValid) {
      return
    }
    const phoneList = contactPhoneSplice.split(',')
    const phoneCountryCode = phoneList[0] ? phoneList[0] : ''
    const contactPhone = phoneList[1] ? phoneList[1] : ''
    const phoneExtensionNumber = phoneList[2] ? phoneList[2] : ''
    const phoneCountryCallingCode = phoneList[3] ? phoneList[3] : ''

    const faxList = contactFaxSplice.split(',')
    const faxCountryCode = faxList[0] ? faxList[0] : ''
    const contactFax = faxList[1] ? faxList[1] : ''
    const faxExtensionNumber = faxList[2] ? faxList[2] : ''
    const faxCountryCallingCode = faxList[3] ? faxList[3] : ''

    this.disableConfirm = true

    const userType = this.userTypeOptions[this.newUserInput.userType]
    try {
      const result = await this.$apollo.mutate({
        mutation: newUser,
        variables: {
          userInput: {
            username,
            password,
            emails: this.emailStrToArr(emails),
            userType,
            contactName,
            contactPhone: delNonNumCode(contactPhone),
            contactFax: delNonNumCode(contactFax),
            contactEmail,
            phoneCountryCode,
            phoneExtensionNumber,
            phoneCountryCallingCode,
            faxCountryCode,
            faxExtensionNumber,
            faxCountryCallingCode
          }
        }
      })
      showNotification({
        msg: 'New user created successfully',
        type: SnackType.Success
      })
      await this.getUsers()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
    this.newUserDialog = false
    this.disableConfirm = false
    this.clearNewUser()
  }

  renameUser (item: any) {
    this.newUserInput = Object.assign({}, item, {
      emails: this.emailArrToStr(item.emails)
    })
    this.renaming = true
  }

  canDelete (item: any) {
    return this.me.userType === 'ROOT' || (item.id === this.me.id) || (item.userType !== 'ROOT' && item.userType !== 'MANAGER')
  }

  async renameConfirm () {
    this.nullUsername = !this.newUserInput.username
    if (this.nullUsername) {
      return
    }
    this.editUserMutate(true)
    this.renaming = false
  }

  async renameCancel () {
    this.renaming = false
    this.clearNewUser()
  }

  async editUser (item: any) {
    this.newUserInput = Object.assign({}, item, {
      password: '',
      passwordRetype: '',
      emails: this.emailArrToStr(item.emails),
      contactName: item.contactName,
      contactPhoneSplice: (item.phoneCountryCode || '') + ',' + (item.contactPhone || '') + ',' + (item.phoneExtensionNumber || '' + ',' + (item.phoneCountryCallingCode || '')),
      contactFaxSplice: (item.faxCountryCode || '') + ',' + (item.contactFax || '') + ',' + (item.faxExtensionNumber || '' + ',' + (item.faxCountryCallingCode || '')),
      contactEmail: item.contactEmail
    })

    this.newUserInput.userType = this.userTypeOptions.findIndex(userType => item.userType === userType)
    this.editUserDialog = true
  }

  async editUserConfirm () {
    if (this.editDialogTab === 0) {
      this.editUserMutate()
    } else {
      this.resetPassword()
    }
  }

  async editUserMutate (notValid = false) {
    const {
      username, emails,
      contactName, contactEmail,
      contactFaxSplice, contactPhoneSplice
    } = this.newUserInput
    if (!notValid) {
      const editValid = (this.$refs.editUserForm as Vue & { validate: () => boolean }).validate()

      if (!editValid) {
        return
      }
    } else {
      if (this.newUserInput.username === '') {
        return
      }
    }
    const phoneList = contactPhoneSplice.split(',')
    const phoneCountryCode = phoneList[0] ? phoneList[0] : ''
    const contactPhone = phoneList[1] ? phoneList[1] : ''
    const phoneExtensionNumber = phoneList[2] ? phoneList[2] : ''
    const phoneCountryCallingCode = phoneList[3] ? phoneList[3] : ''

    const faxList = contactFaxSplice.split(',')
    const faxCountryCode = faxList[0] ? faxList[0] : ''
    const contactFax = faxList[1] ? faxList[1] : ''
    const faxExtensionNumber = faxList[2] ? faxList[2] : ''
    const faxCountryCallingCode = faxList[3] ? faxList[3] : ''

    const userType = this.userTypeOptions[this.newUserInput.userType]
    this.disableConfirm = true
    try {
      const result = await this.$apollo.mutate({
        mutation: editUser,
        variables: {
          userId: Number(this.newUserInput.id),
          userInput: {
            username,
            emails: this.emailStrToArr(emails),
            userType,
            contactName,
            contactPhone: delNonNumCode(contactPhone),
            contactFax: delNonNumCode(contactFax),
            contactEmail,
            phoneCountryCode,
            phoneExtensionNumber,
            phoneCountryCallingCode,
            faxCountryCode,
            faxExtensionNumber,
            faxCountryCallingCode
          }
        }
      })
      this.editUserDialog = false
      this.disableConfirm = false
      this.clearNewUser()
      showNotification({
        msg: 'User edited successfully',
        type: SnackType.Success
      })
      this.getUsers()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async resetPassword () {
    const valid = (this.$refs.resetPasswordForm as Vue & { validate: () => boolean }).validate()
    if (!valid) {
      return
    }
    this.disableConfirm = true
    try {
      const result = await this.$apollo.mutate({
        mutation: resetUserPassword,
        variables: {
          userId: Number(this.newUserInput.id),
          newPassword: this.newUserInput.password
        }
      })
      const res = result.data.resetUserPassword
      if (res) {
        showNotification({
          msg: `Reset user ${this.newUserInput.username}'s password successfully`,
          type: SnackType.Success
        })
        this.clearNewUser()
        await this.getUsers()
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
    this.editUserDialog = false
    this.disableConfirm = false
  }

  deleteUser (item: any) {
    this.newUserInput = Object.assign({}, item, {
      password: '',
      passwordRetype: ''
    })
    this.deleteDialog = true
  }

  async deleteUserConfirm () {
    this.disableConfirm = true
    try {
      const result = await this.$apollo.mutate({
        mutation: deleteUserById,
        variables: {
          userId: Number(this.newUserInput.id)
        }
      })
      const res = result.data.deleteUserById
      if (res) {
        this.getUsers()
        showNotification({
          msg: 'User deleted successfully',
          type: SnackType.Success
        })
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
    this.disableConfirm = false
    this.deleteDialog = false
  }

  clearNewUser () {
    this.newUserInput = {
      id: '',
      username: '',
      emails: '',
      password: '',
      passwordRetype: '',
      userType: -1,
      contactName: '',
      contactPhoneSplice: '',
      contactFaxSplice: '',
      contactEmail: ''
    }
  }

  sortTable (column: any) {
    this.tableOptions.sortBy = column.property
    this.tableOptions.sortDesc = column.order === 'desc'
    this.getUsers()
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getUsers()
  }

  emailArrToStr (emails:string[]) {
    const str = emails.reduce((acc, cur) => {
      return cur + ', ' + acc
    }, '')
    return str.substring(0, str.length - 2)
  }
  emailStrToArr (emailsStr: string) {
    return emailsStr.split(' ').join('').split(',')
  }
}
